import { _ } from "../../polyglot/polyglot.js";
import { isNumber } from "@foretagsplatsen/core-helpers/number";
import { isStringInteger } from "../../helpers/string-util.js";
import { isValidPhoneNumber } from "libphonenumber-js";
import numbro from "numbro";

/**
 * Validator that checks that a value has been provided.
 *
 * @return {function}
 */
export function required() {
	return function (value) {
		if (value) {
			return;
		}
		throw _("Field is required");
	};
}

/**
 * Return a validator that checks that the value is not blank after trimming.
 *
 * @return {function}
 */
export function notBlank() {
	return function (value) {
		const trimmed = value?.trim();
		if (trimmed) {
			return;
		}
		throw _("Can not be blank");
	};
}

/**
 * Return a validator that checks that the value is not longer than the provided
 * maximum length.
 *
 * @param {number} max - The maximum length allowed
 * @return {function}
 */
export function maxLength(max) {
	return function (value) {
		if (!value || value.length <= max) {
			return;
		}
		throw _(
			"The text is {curLength} characters long but the maximum is {maxLength}.",
			{
				curLength: value.length,
				maxLength: max,
			},
		);
	};
}

/**
 * Return a validator that checks that the value is not strictly shorter than
 * the provided minimum length nor strincly longer than the provided maximum
 * length.
 *
 * @param {number} min - The minimum length allowed
 * @param {number} max - The maximum length allowed
 */
export function rangeLength(min, max) {
	return function (value) {
		if (!value) {
			throw _("Can not be blank");
		}
		if (value.length < min || value.length > max) {
			throw _(
				"The text is {curLength} characters long but must fit between {min} and {max}.",
				{
					curLength: value.length,
					min,
					max,
				},
			);
		}
	};
}

/**
 * Return a validator that checks that the value is a valid number.
 *
 * @param {object} options - Object parameters
 * @param {boolean} options.strict - If true, the value must represent strictly
 *   a number (e.g., "123"). If false, the value can represent a number among
 *   other characters (e.g., "123k")
 */
export function validNumber({ strict = false } = {}) {
	return function (value) {
		value = strict ? +value : numbro.unformat(value);
		if (isNumber(value)) {
			return;
		}
		throw _("Must be a number");
	};
}

/**
 * Return a validator that checks that the value is a valid integer.
 *
 * @return {function}
 */
export function validInteger() {
	return function (value) {
		if (isStringInteger(value)) return;
		throw _("Must be an integer");
	};
}

const alphaNumRegex = /^\w+$/;

/**
 * Return a validator that checks that the value is alphanumeric.
 *
 * @return {function}
 */
export function alphaNum() {
	return function (value) {
		if (!value || alphaNumRegex.test(value)) {
			return;
		}
		throw _("Must be alphanumeric, only letters and digits are allowed.");
	};
}

const uppercaseLettersAndDigitsRegex = /^[\dA-Z]+$/;

/**
 * Return a validator that checks that the value is composed of uppercase
 * letters or digits only.
 *
 * @return {function}
 */
export function upperCaseLettersAndDigits() {
	return function (value) {
		if (!value || uppercaseLettersAndDigitsRegex.test(value)) {
			return;
		}
		throw _("Must be upper case letters or digits.");
	};
}

/**
 * Return a validator that checks that the value is composed of lowercase
 * letters only.
 *
 * @return {function}
 */
export function lowerCase() {
	return function (letters) {
		if (letters === letters.toLowerCase()) {
			return;
		}

		throw _("Must be lower case.");
	};
}

const firstLowerCaseLetterRegex = /^[a-z]/;

/**
 * Return a validator that checks that the value starts with a lowercase letter.
 *
 * @return {function}
 */
export function firstLowerCaseLetter() {
	return function (letters) {
		if (firstLowerCaseLetterRegex.test(letters[0])) {
			return;
		}

		throw _("Must start with a lower case character.");
	};
}

// eslint-disable-next-line sonarjs/slow-regex -- don't want to change that now
const validEmailRegex = /^\S+@\S+\.\S+$/;

/**
 * Return a validator that checks that the value is a valid email.
 *
 * @return {function}
 */
export function validEmail() {
	return function (value) {
		if (!value || validEmailRegex.test(value)) {
			return;
		}
		throw _("Must be a valid email");
	};
}

/**
 * Return a validator that checks that the value is a valid phone number.
 *
 * @return {function}
 */
export function validInternationalPhoneNumber() {
	return function (value) {
		if (!value) return;

		const isInternationalNumber = value[0] === "+";

		if (isInternationalNumber && isValidPhoneNumber(value)) {
			return;
		}

		throw _(
			"Your phone number should be valid, start with a '+' and include the country code",
		);
	};
}

const validUrlRegex =
	// eslint-disable-next-line sonarjs/slow-regex -- don't want to change that now
	/^(https?:\/\/)?([\d.a-z-]+)\.([.a-z]{2,6})([\w ./-])*\/?$/;

/**
 * Return a validator that checks that the value is a valid URL.
 *
 * @return {function}
 */
export function validUrl() {
	return function (value) {
		if (!value || validUrlRegex.test(value)) {
			return;
		}
		throw _("Must be a valid URL");
	};
}

const validStrictUrlRegex =
	// eslint-disable-next-line sonarjs/slow-regex -- don't want to change that now
	/^(https?:\/\/)+([\d.a-z-]+)\.([.a-z]{2,6})([\w ./-])*\/?$/;

/**
 * Return a validator that checks that the value is a valid URL starting with
 * either http:// or https://.
 *
 * @return {function}
 */
export function validStrictUrl() {
	return function (value) {
		if (!value || validStrictUrlRegex.test(value)) {
			return;
		}
		throw _("Must be a valid URL starting with http:// or https://");
	};
}

/**
 * Return a validator that checks that the value matches the provided regular
 * expression.
 *
 * @param {RegExp} expression - The regular expression to match
 * @param {string} message - The error message to throw if the value does not
 *   match
 * @return {function}
 */
export function matchRegExp(expression, message) {
	return function (value) {
		if (value && expression.test(value)) {
			return;
		}
		throw message;
	};
}

/**
 * Return a validator that checks that the value is greater than the provided
 * minimum value.
 *
 * @param {number} min - The minimum value allowed
 * @return {function}
 */
export function minValue(min) {
	return function (value) {
		if (!value || Number(value) >= min) {
			return;
		}
		throw _("Must be number greater than {min}", { min });
	};
}

/**
 * Return a validator that checks that the value is less than the provided
 * maximum value and greater than the provided minimum value.
 *
 * @param {number} min - The minimum value allowed
 * @param {number} max - The maximum value allowed
 * @return {function}
 */
export function rangeValue(min, max) {
	return function (value) {
		if (value && Number(value) >= min && Number(value) <= max) {
			return;
		}
		throw _("Must be a number between {min} and {max}", { min, max });
	};
}

/**
 * Return a validator that checks a collection of validators.
 */
export function validateAll(...validators) {
	return function (value) {
		validators.forEach((validator) => {
			validator(value);
		});
	};
}

/**
 * Execute all the validators only if the condition predicate returns true.
 *
 * @param {function} condition - the condition predicate
 */
export function validateAllWhen(condition, ...validators) {
	return function (value) {
		if (condition() !== true) {
			return;
		}

		validators.forEach((validator) => {
			validator(value);
		});
	};
}

/**
 * It wraps the validator and stores its last thrown error.
 * Then it provides a `getLastError`method to retrieve the stored error.
 *
 * @param {function} validator - a validator function
 */
export function validatorWithLastError(validator) {
	let lastError = null;

	const fn = (input) => {
		try {
			validator(input);
		} catch (error) {
			lastError = error;
			throw error;
		}
	};

	fn.getLastError = () => lastError;
	return fn;
}

/**
 * Throws error message if the input is not a string of numbers
 * which are separated by commas.
 *
 * @return {function}
 */
export function isCommaSeparatedNumbers() {
	return function (string) {
		const numbers = string.split(",");
		for (const number of numbers) {
			if (isNaN(Number(number))) {
				throw _("Please enter numbers separated by commas");
			}
		}
	};
}

/**
 * Return a validator that throws an error message if the input is not a string
 * of accounts (interval or single ones) which are separated by commas.
 */
export function isCommaSeparatedAccountIntervalsOrNumbers() {
	return function (string) {
		const accounts = string.split(",");

		accounts.forEach((account) => {
			if (isStringInteger(account)) return;
			if (isAccountInterval(account)) return;
			throw _("Please enter valid account codes (e.g., 2641,2643-2651)");
		});
	};
}

/**
 * Return a validator that throws error message if the sum of numbers given
 * as a string is not equal sumTotal
 *
 * @param {number} sumTotal - The number which the sum of numbers in
 *   string is equal to
 * @return {function}
 */
export function isSumEqualNumber(sumTotal) {
	return function (string) {
		const numbers = string.split(",").map((element) => +element);
		const sumOfValues = numbers.reduce(
			(previousValue, currentValue) => previousValue + currentValue,
		);

		if (sumOfValues === sumTotal) {
			return;
		}
		throw _("Sum of comma separated values must equal {sumTotal}", {
			sumTotal,
		});
	};
}

function isAccountInterval(string) {
	const numbers = string.split("-");
	if (numbers.length !== 2) return false;

	return numbers.every(isStringInteger);
}
